import { getInventoryConfig } from "@/api/inventory/getConfig";
import { useListNFTMutation } from "@/api/marketplace/createOrder";
import { useSeaport } from "@/hooks/useSeaport";
import {
	Button,
	type ButtonProps,
	Group,
	Modal,
	NumberInput,
	SegmentedControl,
	Stack,
	Text,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { ItemType } from "@opensea/seaport-js/lib/constants";
import type { CreateInputItem } from "@opensea/seaport-js/lib/types";
import { useAppKitAccount } from "@reown/appkit/react";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { parseUnits } from "viem";
import { z } from "zod";

const formSchema = z.object({
	price: z.number().gt(0, "Price must be greater than 0"),
	amount: z.number().min(1, "Amount must be greater than 0").optional(),
	startTime: z.number().gte(0, "Start time must be greater than or equal to 0"),
	endTime: z.number().gte(0, "End time must be greater than or equal to 0"),
	token: z.enum(["usdc", "credits"]),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	price: 1,
	amount: 1,
	startTime: 0,
	endTime: 0,
	token: "usdc",
};

export function ListToMarketplaceButton({
	type,
	name,
	collection,
	tokenId,
	...buttonProps
}: {
	type: "ERC721A" | "ERC1155";
	name: string;
	collection: string;
	tokenId: number;
} & ButtonProps) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { createSellOrder } = useSeaport();
	const { address } = useAppKitAccount();

	const config = useQuery(getInventoryConfig());
	const listNFTOnMarketplace = useListNFTMutation();

	const form = useForm({
		defaultValues: {
			...DEFAULT_VALUES,
			startTime: Math.floor(Date.now() / 1000),
			endTime: Math.floor(Date.now() / 1000) + 365 * 60 * 60 * 24,
		},
		validators: {
			onChange: formSchema,
		},
		onSubmit: async ({ value }) => {
			const tokenAddress = config.data?.tokenAddresses?.[value.token];
			if (!tokenAddress) {
				toast.error("Failed to get token address");
				throw new Error("Failed to get token address");
			}

			const priceInWei = parseUnits(value.price.toString(), 6).toString();

			let offer: CreateInputItem[];
			if (type === "ERC1155") {
				if (!value.amount) {
					throw new Error("Amount is required");
				}
				offer = [
					{
						itemType: ItemType.ERC1155,
						amount: value.amount.toString(),
						identifier: tokenId.toString(),
						token: collection,
					},
				];
			} else {
				offer = [
					{
						itemType: ItemType.ERC721,
						identifier: tokenId.toString(),
						token: collection,
					},
				];
			}

			const order = await createSellOrder.mutateAsync({
				offer,
				consideration: [
					{
						amount: priceInWei,
						token: tokenAddress,
						recipient: address,
					},
				],
				startTime: Number(value.startTime),
				endTime: Number(value.endTime),
				allowPartialFills: false,
			});

			if (!order) {
				throw new Error("Failed to create order");
			}
			await listNFTOnMarketplace.mutateAsync({
				signature: order.signature,
				parameters: {
					...order.parameters,
					startTime: Number(order.parameters.startTime),
					endTime: Number(order.parameters.endTime),
					totalOriginalConsiderationItems: Number(
						order.parameters.totalOriginalConsiderationItems,
					),
					counter: Number(order.parameters.counter),
				},
			});
			handleClose(true);
		},
	});

	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const isSubmitted = useStore(form.store, (state) => state.isSubmitted);

	const handleClose = (forceClose?: boolean) => {
		setIsModalOpen(false);
		if (!isSubmitting || forceClose) {
			setTimeout(() => {
				listNFTOnMarketplace.reset();
				createSellOrder.reset();
				form.reset();
			}, 1000);
		}
	};

	return (
		<>
			<Button {...buttonProps} onClick={() => setIsModalOpen(true)}>
				List to Marketplace
			</Button>
			<Modal
				opened={isModalOpen}
				onClose={handleClose}
				title={
					<Text size="h5" fw={800}>
						List to Marketplace
					</Text>
				}
				size="lg"
				radius="md"
				padding={0}
				styles={{
					header: {
						paddingBottom: "1rem",
						minHeight: "auto",
					},
				}}
			>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						void form.handleSubmit();
					}}
				>
					<Stack gap="md">
						<Text size="sm" c="dimmed" style={{ whiteSpace: "pre-line" }}>
							Please enter the price to list this NFT to the Marketplace:
						</Text>

						{type === "ERC1155" && (
							<form.Field name="amount">
								{(field) => (
									<NumberInput
										label="Amount"
										placeholder="Enter amount"
										value={field.state.value}
										onChange={(value) => field.handleChange(Number(value) || 1)}
										error={!!field.state.meta.errors[0]}
										allowDecimal={false}
										min={1}
										step={1}
										required
									/>
								)}
							</form.Field>
						)}

						<form.Field name="price">
							{(field) => (
								<NumberInput
									label="Price"
									placeholder="Enter amount"
									value={field.state.value}
									onChange={(value) => field.handleChange(Number(value) || 1)}
									error={!!field.state.meta.errors[0]}
									min={0}
									step={0.1}
									allowDecimal
									required
								/>
							)}
						</form.Field>

						<form.Field name="token">
							{(field) => (
								<Group>
									<SegmentedControl
										data={[
											{ label: "USDC", value: "usdc" },
											// { label: "Credits", value: "credits" },
										]}
										value={field.state.value}
										onChange={(value) =>
											field.handleChange(value as FormValues["token"])
										}
									/>
								</Group>
							)}
						</form.Field>
						<form.Field name="startTime">
							{(field) => (
								<DateTimePicker
									value={
										field.state.value
											? new Date(field.state.value * 1000).toISOString()
											: null
									}
									onChange={(value: string | null) => {
										if (value) {
											const date = new Date(value);
											const utcSeconds = Math.floor(date.getTime() / 1000);
											field.handleChange(utcSeconds);
										}
									}}
									label={`Pick start time (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
									placeholder="Pick start time"
									minDate={new Date()}
									withAsterisk
									timePickerProps={{
										withDropdown: true,
										popoverProps: {
											withinPortal: false,
										},
										format: "12h",
									}}
								/>
							)}
						</form.Field>
						<form.Field name="endTime">
							{(field) => (
								<DateTimePicker
									value={
										field.state.value
											? new Date(field.state.value * 1000).toISOString()
											: null
									}
									onChange={(value: string | null) => {
										if (value) {
											const date = new Date(value);
											const utcSeconds = Math.floor(date.getTime() / 1000);
											field.handleChange(utcSeconds);
										}
									}}
									label={`Pick end time (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
									placeholder="Pick end time"
									minDate={new Date()}
									withAsterisk
									timePickerProps={{
										withDropdown: true,
										popoverProps: {
											withinPortal: false,
										},
										format: "12h",
									}}
								/>
							)}
						</form.Field>

						<pre
							style={{
								margin: 0,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
							}}
						>
							<Text size="xs" c="red" style={{ whiteSpace: "pre-line" }}>
								{createSellOrder.isError &&
									createSellOrder.error.message.slice(0, 300)}
								{listNFTOnMarketplace.isError &&
									listNFTOnMarketplace.error.message.slice(0, 300)}
							</Text>
						</pre>

						<Group justify="flex-end" mt="md">
							<Button variant="light" onClick={() => handleClose(true)}>
								Cancel
							</Button>
							<Button
								type="submit"
								loading={isSubmitting}
								disabled={!canSubmit || isSubmitted}
							>
								{isSubmitted ? "Submitted" : "List"}
							</Button>
						</Group>
					</Stack>
				</form>
			</Modal>
		</>
	);
}
